import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import SubHeaders from '@/components/Headers/SubHeaders'
import { useConfirmation } from '@/contexts/confirmation'
import CommissionPolicyList from './List'
import PolicyFormModal from './Form'
import { PolicyFormData } from './Form/schema'
import { CommissionPolicy, PolicyReferrerType } from '@/modules/Affiliate/types'
import {
    createCommissionPolicy,
    updateCommissionPolicy,
    deleteCommissionPolicy,
} from '@/modules/Affiliate/services'
import { paths } from '@/utils/path'
import AddIcons from '@/icons/AddIcons'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import useAffiliatePolicyPermissions from '../../hooks/useAffiliatePolicyPermissions'

interface CommissionPoliciesProps {
    embedded?: boolean
    modalOpen?: boolean
    setModalOpen?: (open: boolean) => void
}

const CommissionPolicies: React.FC<CommissionPoliciesProps> = ({
    embedded = false,
    modalOpen: controlledModalOpen,
    setModalOpen: setControlledModalOpen,
}) => {
    const { t } = useTranslation('affiliate')
    const { setConfirmation } = useConfirmation()
    const queryClient = useQueryClient()
    const { canCreatePolicy, canUpdatePolicy, canDeletePolicy } =
        useAffiliatePolicyPermissions()

    const [internalModalOpen, setInternalModalOpen] = useState(false)
    const [selectedPolicy, setSelectedPolicy] =
        useState<CommissionPolicy | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const modalOpen = controlledModalOpen ?? internalModalOpen
    const setModalOpen = setControlledModalOpen ?? setInternalModalOpen

    const handleAddPolicy = () => {
        if (!canCreatePolicy) return
        setSelectedPolicy(null)
        setModalOpen(true)
    }

    const handleEditPolicy = (policy: CommissionPolicy) => {
        if (!canUpdatePolicy) return
        setSelectedPolicy(policy)
        setModalOpen(true)
    }

    const handleCloseModal = () => {
        setModalOpen(false)
        setSelectedPolicy(null)
    }

    const handleSubmit = async (data: PolicyFormData) => {
        if (selectedPolicy ? !canUpdatePolicy : !canCreatePolicy) return
        setIsSubmitting(true)
        // Format dates to ISO 8601 for Go time.Time parsing
        const effectiveDate = data.effective_date
            ? dayjs(data.effective_date).startOf('day').toISOString()
            : undefined
        const expiredDate = data.expired_date
            ? dayjs(data.expired_date).endOf('day').toISOString()
            : undefined

        // The API rejects "global with affiliates" and "targeted at nobody", so send exactly
        // one of the two shapes.
        const ownership = data.is_global
            ? { is_global: true, affiliate_ids: [] }
            : { is_global: false, affiliate_ids: data.affiliate_ids || [] }

        // Audience filter: '' referrer_type means "everyone" -> send null so the API clears it.
        const referrerType = data.referrer_type
            ? (data.referrer_type as PolicyReferrerType)
            : null
        const departmentIds =
            data.referrer_type === 'employee' ? data.department_ids || [] : []

        try {
            if (selectedPolicy) {
                await updateCommissionPolicy({
                    policy_id: selectedPolicy.policy_id,
                    payload: {
                        ...ownership,
                        scope: data.scope,
                        product_group_id: data.product_group_id,
                        product_id: data.product_id,
                        policy_name: data.policy_name,
                        commission_percent: data.commission_percent,
                        effective_date: effectiveDate,
                        expired_date: expiredDate,
                        priority: data.priority,
                        description: data.description || undefined,
                        update_audience: true,
                        referrer_type: referrerType,
                        department_ids: departmentIds,
                    },
                })
                toast.success(t('policy_updated_successfully'))
            } else {
                await createCommissionPolicy({
                    ...ownership,
                    policy_name: data.policy_name,
                    scope: data.scope,
                    commission_percent: data.commission_percent,
                    product_group_id: data.product_group_id,
                    product_id: data.product_id,
                    effective_date: effectiveDate,
                    expired_date: expiredDate,
                    priority: data.priority,
                    description: data.description || undefined,
                    referrer_type: referrerType,
                    department_ids: departmentIds,
                })
                toast.success(t('policy_created_successfully'))
            }
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.AFFILIATE_COMMISSION_POLICIES],
            })
            handleCloseModal()
        } catch (error) {
            console.error('Failed to save policy:', error)
            toast.error(t('save_policy_error'))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleDeletePolicy = (policy: CommissionPolicy) => {
        if (!canDeletePolicy) return
        setConfirmation({
            open: true,
            title: t('delete_policy'),
            description: t('confirm_delete_policy'),
            onConfirm: async () => {
                try {
                    await deleteCommissionPolicy(policy.policy_id)
                    toast.success(t('policy_deleted_successfully'))
                    queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.AFFILIATE_COMMISSION_POLICIES],
                    })
                } catch (error) {
                    console.error('Failed to delete policy:', error)
                    toast.error(t('save_policy_error'))
                }
            },
        })
    }

    const handleTogglePolicy = async (policy: CommissionPolicy) => {
        if (!canUpdatePolicy) return
        try {
            await updateCommissionPolicy({
                policy_id: policy.policy_id,
                payload: { is_active: !policy.is_active },
            })
            toast.success(t('policy_updated_successfully'))
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.AFFILIATE_COMMISSION_POLICIES],
            })
        } catch (error) {
            console.error('Failed to toggle policy:', error)
            toast.error(t('save_policy_error'))
        }
    }

    return (
        <div>
            {!embedded && (
                <SubHeaders
                    actions={
                        canCreatePolicy
                            ? [
                                  {
                                      icon: <AddIcons />,
                                      label: t('add_policy'),
                                      onClick: handleAddPolicy,
                                  },
                              ]
                            : []
                    }
                    breadcrumbs={[
                        {
                            path: paths.affiliatesList,
                            title: t('affiliate_manager'),
                        },
                        {
                            path: paths.affiliateCommissionPolicies,
                            title: t('commission_policies'),
                        },
                    ]}
                />
            )}
            <CommissionPolicyList
                onEditPolicy={handleEditPolicy}
                onDeletePolicy={handleDeletePolicy}
                onTogglePolicy={handleTogglePolicy}
                canUpdatePolicy={canUpdatePolicy}
                canDeletePolicy={canDeletePolicy}
            />
            {(canCreatePolicy || canUpdatePolicy) && (
                <PolicyFormModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    policy={selectedPolicy}
                    onSubmit={handleSubmit}
                    isLoading={isSubmitting}
                    allowOwnerSelection
                />
            )}
        </div>
    )
}

export default CommissionPolicies
